import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { isGlobalAdmin, hasCap, WS } from '@/lib/workspaces';
import { APP_ICONS } from '@/lib/appIcons';
import { TOTAL_E_SLOGAN } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

// Reusable mobile→desktop login template for the Total-E portal family.
// Only the icon, slogan, portal name and access-contact wording vary between
// portals (Management, Client, TEAM, Partners). Card dimensions, icon display
// sizes, typography, spacing, controls and footer placement are shared so every
// portal matches at equivalent viewport sizes. Authentication is the existing
// /auth/login — this template never assigns roles or grants permissions.

const GREEN = '#5E9A2F';
const TOTAL_TECH_LOGO = '/tfs-connect/total-technologies.png';   // transparent, division wording embedded

// Shared, portal-agnostic interface strings. Brand names are intentionally kept
// in English. The copyright is a fixed legal/proprietary notice, identical on
// every device and language per spec.
const COPYRIGHT = '© 2026 Total Facility Solutions, Inc. Proprietary application management system. All rights reserved.';

const UI = {
  en: {
    welcome: 'Welcome',
    signin: 'Sign in to continue',
    email: 'Email',
    password: 'Password',
    forgot: 'Forgot password?',
    signInBtn: 'Sign In',
    signingIn: 'Signing in…',
    forgotToast: 'Password resets are handled by your administrator. Please contact your TFS administrator for assistance.',
    loginOk: 'Login successful!',
    loginFail: 'Login failed',
    showPw: 'Show password',
    hidePw: 'Hide password',
    langLabel: 'Language',
  },
  es: {
    welcome: 'Bienvenido',
    signin: 'Inicia sesión para continuar',
    email: 'Correo electrónico',
    password: 'Contraseña',
    forgot: '¿Olvidaste tu contraseña?',
    signInBtn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión…',
    forgotToast: 'El restablecimiento de contraseñas lo gestiona tu administrador. Comunícate con tu administrador de TFS para obtener ayuda.',
    loginOk: '¡Inicio de sesión exitoso!',
    loginFail: 'Error al iniciar sesión',
    showPw: 'Mostrar contraseña',
    hidePw: 'Ocultar contraseña',
    langLabel: 'Idioma',
  },
};

// Portal-specific configuration. Add TEAM / Partners here when their screens
// are built — the layout below stays identical.
export const PORTALS = {
  management: {
    key: 'management',
    iconSrc: '/total_e_icon.webp',
    iconAlt: 'total e',
    // Approved Total-E brand slogan — used verbatim and kept in English in BOTH languages
    // (the brand name "Total-E" is never translated).
    slogan: {
      en: TOTAL_E_SLOGAN,
      es: TOTAL_E_SLOGAN,
    },
    portalName: { en: 'Management Portal', es: 'Portal de Administración' },
    accessNotice: {
      en: 'Access is provisioned by Total Facility Solutions. Contact your TFS administrator for access.',
      es: 'El acceso es proporcionado por Total Facility Solutions. Comunícate con tu administrador de TFS para obtener acceso.',
    },
    testid: 'tfs-internal-login-page',
    bg: {
      color: '#0A1B2E',
      image: '/wallpaper_clean.png',
      imageOpacity: 1,
      overlay: 'linear-gradient(90deg, rgba(9,24,45,0.78) 0%, rgba(9,24,45,0.5) 45%, rgba(9,24,45,0.4) 100%)',
    },
  },
  client: {
    key: 'client',
    iconSrc: APP_ICONS.rout_client,
    iconAlt: 'ROUT by TFS',
    slogan: {
      en: 'Your Facility. On ROUT.',
      es: 'Tu instalación. En ROUT.',
    },
    portalName: { en: 'Client Portal', es: 'Portal de Clientes' },
    accessNotice: {
      en: 'Access is provisioned by Total Facility Solutions. Contact your Facilities Director for access.',
      es: 'El acceso es proporcionado por Total Facility Solutions. Comunícate con tu Director de Instalaciones para obtener acceso.',
    },
    testid: 'rout-client-login-page',
    bg: {
      color: '#081C31',
      gradient: 'linear-gradient(150deg, #0E2A47 0%, #081C31 100%)',
      image: '/rout_login_map.jpg',
      imageOpacity: 0.18,
      routeLines: true,
    },
  },
  team: {
    key: 'team',
    iconSrc: APP_ICONS.team,
    iconAlt: 'TEAM',
    // Exact English slogan, kept in English in BOTH languages (not translated).
    slogan: {
      en: 'Total Engagement, Access & Management',
      es: 'Total Engagement, Access & Management',
    },
    portalName: { en: 'Team Portal', es: 'Portal del equipo' },
    accessNotice: {
      en: 'Access is provisioned by Total Facility Solutions. Contact your TFS administrator for access.',
      es: 'El acceso es proporcionado por Total Facility Solutions. Comunícate con tu administrador de TFS para obtener acceso.',
    },
    testid: 'tfs-team-login-page',
    bg: {
      color: '#081024',
      image: '/team_wallpaper_mobile.png',   // portrait — stacked mobile/tablet
      imageWide: '/team_wallpaper_desktop.png', // landscape — desktop two-column
      imageOpacity: 1,
      overlay: 'linear-gradient(180deg, rgba(6,14,32,0.35) 0%, rgba(6,14,32,0.12) 42%, rgba(6,14,32,0.30) 100%)',
    },
    // TEAM app destination is not built yet. Only existing administrators may
    // enter the ecosystem via this entrance; everyone else (valid creds) sees a
    // "coming soon" notice and is NOT routed into the management hub. Decision is
    // made from existing capabilities — no hardcoded email, no auth bypass.
    gate: {
      allow: (user) => isGlobalAdmin(user) || hasCap(user, WS.TEAM),
      blockedMessage: { en: 'TEAM is coming soon.', es: 'TEAM estará disponible próximamente.' },
    },
  },
  partners: {
    key: 'partners',
    iconSrc: APP_ICONS.partners,
    iconAlt: 'Partners',
    // Approved Partners slogan (English + Spanish).
    slogan: {
      en: 'Local expertise, total coverage.',
      es: 'Experiencia local, cobertura total.',
    },
    portalName: { en: 'Partners Portal', es: 'Portal de Colaboradores' },
    accessNotice: {
      en: 'Access is provisioned by Total Facility Solutions. Contact your TFS administrator for access.',
      es: 'El acceso es proporcionado por Total Facility Solutions. Comunícate con tu administrador de TFS para obtener acceso.',
    },
    testid: 'tfs-partners-login-page',
    bg: {
      color: '#07122a',
      image: '/partners_wallpaper_mobile.png',   // portrait — stacked mobile/tablet
      imageWide: '/partners_wallpaper_desktop.png', // landscape — desktop two-column
      imageOpacity: 1,
      overlay: 'linear-gradient(180deg, rgba(5,12,28,0.30) 0%, rgba(5,12,28,0.10) 45%, rgba(5,12,28,0.28) 100%)',
    },
    gate: {
      allow: (user) => isGlobalAdmin(user),
      blockedMessage: { en: 'Partners Portal is coming soon.', es: 'El Portal de Colaboradores estará disponible próximamente.' },
    },
  },
};

// ROUT background route-lines + markers overlay (restrained, decorative).
const RouteLines = () => (
  <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.22]"
    preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 800" fill="none" aria-hidden="true">
    <path d="M-40 620 C 240 520, 420 690, 640 540 S 980 320, 1260 420" stroke="#8FC93A" strokeWidth="2" strokeDasharray="14 16" />
    <path d="M-40 300 C 280 200, 460 360, 740 240 S 1040 120, 1260 210" stroke="#ffffff" strokeWidth="1.6" strokeDasharray="8 18" />
    <circle cx="640" cy="540" r="6" fill="#8FC93A" />
    <circle cx="740" cy="240" r="5" fill="#ffffff" />
    <circle cx="1040" cy="150" r="4" fill="#8FC93A" />
  </svg>
);

const LANG_KEY = 'portal_lang';
const initLang = () => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    return saved === 'es' ? 'es' : 'en'; // default English
  } catch { return 'en'; }
};

const PortalLogin = ({ portal = 'management' }) => {
  const cfg = PORTALS[portal] || PORTALS.management;
  const { login } = useContext(AuthContext);
  const [lang, setLang] = useState(initLang);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ttOk, setTtOk] = useState(true);
  const t = UI[lang];

  useEffect(() => { try { localStorage.setItem(LANG_KEY, lang); } catch { /* noop */ } }, [lang]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginData);
      const userData = res.data.user;
      // Portal access gate (e.g. TEAM not yet available): valid credentials that
      // are not yet authorized for this portal's destination see a localized
      // notice and are NOT signed into any other hub.
      if (cfg.gate && !cfg.gate.allow(userData)) {
        toast.info(cfg.gate.blockedMessage[lang]);
        setLoading(false);
        return;
      }
      login(res.data.token, userData);
      toast.success(t.loginOk);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.loginFail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = (e) => { e.preventDefault(); toast.info(t.forgotToast); };

  const bg = cfg.bg || {};
  return (
    <div
      className="relative w-full overflow-y-auto"
      style={{ minHeight: '100dvh', background: bg.gradient || undefined, backgroundColor: bg.color || '#0A1B2E' }}
      data-testid={cfg.testid || 'tfs-internal-login-page'}
    >
      {/* Portal background: optional wallpaper/map image (with an optional wide
          variant for desktop two-column), decorative route lines (ROUT), and a
          readability overlay — all directly on the backdrop. */}
      {bg.image && (
        <div className={`pointer-events-none absolute inset-0 bg-cover bg-center ${bg.imageWide ? 'lg:hidden' : ''}`} aria-hidden="true"
          style={{ backgroundImage: `url(${bg.image})`, opacity: bg.imageOpacity ?? 1 }} />
      )}
      {bg.imageWide && (
        <div className="pointer-events-none absolute inset-0 hidden bg-cover bg-center lg:block" aria-hidden="true"
          style={{ backgroundImage: `url(${bg.imageWide})`, opacity: bg.imageOpacity ?? 1 }} />
      )}
      {bg.routeLines && <RouteLines />}
      {bg.overlay && (
        <div className="absolute inset-0" aria-hidden="true" style={{ background: bg.overlay }} />
      )}

      <div
        className="relative z-10 flex flex-col"
        style={{
          minHeight: '100dvh',
          paddingTop: 'max(env(safe-area-inset-top), clamp(0.6rem, 1.6dvh, 1.25rem))',
          paddingBottom: 'max(env(safe-area-inset-bottom), clamp(0.6rem, 1.6dvh, 1.5rem))',
          paddingLeft: 'max(env(safe-area-inset-left), 1.25rem)',
          paddingRight: 'max(env(safe-area-inset-right), 1.25rem)',
        }}
      >
        {/* Language toggle — provided at the entry point, defaults to English */}
        <div className="flex justify-end" role="group" aria-label={t.langLabel}>
          <div className="inline-flex overflow-hidden rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
            {['en', 'es'].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)}
                data-testid={`login-lang-${l}`} aria-pressed={lang === l}
                className={`px-3 py-1 text-xs font-bold transition-colors ${lang === l ? 'bg-white text-[#0E2A47]' : 'text-white/80'}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Main composition — stacked on narrow screens, two columns on desktop */}
        <main className="flex w-full flex-1 flex-col items-center justify-center">
          <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-[clamp(0.6rem,1.8dvh,1.5rem)] lg:grid-cols-2 lg:gap-14">
            {/* Branding: icon + slogan centered underneath */}
            <section className="flex flex-col items-center text-center" data-testid="login-branding">
              <img
                src={cfg.iconSrc}
                alt={cfg.iconAlt}
                className="w-auto object-contain drop-shadow-xl h-[clamp(72px,12.5dvh,176px)] lg:h-[clamp(180px,22dvh,256px)]"
                draggable={false}
                data-testid="login-total-e-icon"
              />
              {cfg.slogan && (
                <p className="mt-[clamp(0.4rem,1.3dvh,1.25rem)] max-w-md font-medium text-slate-100 text-[clamp(1rem,2.4dvh,1.25rem)] lg:text-2xl">
                  {cfg.slogan[lang]}
                </p>
              )}
            </section>

            {/* Sign-in card */}
            <section className="flex w-full justify-center lg:justify-start">
              <div
                className="w-full max-w-md rounded-3xl bg-[#F5F3ED] shadow-2xl ring-1 ring-black/5"
                style={{ padding: 'clamp(0.9rem, 2.2dvh, 2rem)' }}
                data-testid="login-card"
              >
                <div className="text-center">
                  <h2 className="font-extrabold text-[#0E2A47] text-[clamp(1.375rem,3dvh,1.875rem)]">{t.welcome}</h2>
                  <p className="mt-1 font-semibold text-[#0E2A47] text-[clamp(0.95rem,2.1dvh,1.125rem)]" data-testid="login-portal-name">{cfg.portalName[lang]}</p>
                  <p className="mt-1 text-slate-500 text-[clamp(0.8rem,1.8dvh,0.9375rem)]">{t.signin}</p>
                </div>

                <form onSubmit={handleLogin} className="flex flex-col" style={{ marginTop: 'clamp(0.8rem, 2dvh, 1.75rem)', gap: 'clamp(0.55rem, 1.5dvh, 1.1rem)' }}>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-sm font-semibold text-[#0E2A47]">{t.email}</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      placeholder="you@yourcompany.com"
                      className="h-11 rounded-xl border-slate-300 bg-white px-4 text-base text-[#0E2A47] placeholder:text-slate-400 focus-visible:ring-[#8FC93A]"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-sm font-semibold text-[#0E2A47]">{t.password}</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        className="h-11 rounded-xl border-slate-300 bg-white pl-4 pr-12 text-base text-[#0E2A47] placeholder:text-slate-400 focus-visible:ring-[#8FC93A]"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        data-testid="password-visibility-toggle"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? t.hidePw : t.showPw}
                        className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-slate-400 transition-colors hover:text-[#0E2A47]"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        data-testid="forgot-password-link"
                        onClick={handleForgot}
                        className="py-1 text-sm font-semibold text-[#5E9A2F] transition-colors hover:text-[#4F8427] hover:underline"
                      >
                        {t.forgot}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    disabled={loading}
                    className="h-11 w-full rounded-xl text-base font-semibold text-white transition-colors hover:brightness-95 disabled:opacity-70"
                    style={{ backgroundColor: GREEN }}
                  >
                    {loading ? t.signingIn : t.signInBtn}
                  </Button>
                </form>

                <p className="mx-auto max-w-sm text-center leading-snug text-slate-500 text-[clamp(0.75rem,1.7dvh,0.8125rem)]" style={{ marginTop: 'clamp(0.7rem, 1.6dvh, 1.5rem)' }} data-testid="login-access-notice">
                  {cfg.accessNotice[lang]}
                </p>
              </div>
            </section>
          </div>
        </main>

        {/* Footer — Total Technologies logo (division wording embedded) directly
            on the wallpaper, sitting just above the copyright disclaimer. */}
        <footer className="flex flex-col items-center text-center" style={{ marginTop: 'clamp(0.6rem, 1.6dvh, 2rem)', gap: 'clamp(0.25rem, 0.7dvh, 0.5rem)' }} data-testid="login-footer">
          {ttOk ? (
            <img
              src={TOTAL_TECH_LOGO}
              alt="Total Technologies — A Total Facility Solutions, Inc. division"
              onError={() => setTtOk(false)}
              className="w-auto object-contain opacity-95 h-[clamp(2.1875rem,4.75dvh,3.75rem)] sm:h-[clamp(3.125rem,6.25dvh,4.375rem)]"
              draggable={false}
            />
          ) : (
            <div className="text-base font-bold tracking-tight text-white/90">Total Technologies</div>
          )}
          <p className="mx-auto max-w-2xl leading-tight text-slate-300/85 text-[clamp(0.6875rem,1.5dvh,0.8125rem)]">
            {COPYRIGHT}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PortalLogin;
